"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { api, type Me } from "@/lib/api";
import { markAppBootReady } from "@/lib/progress";
import { LOCALES, LOCALE_LABEL, t, type Locale } from "@/lib/i18n";
import { cn, Logo, SwitchRow } from "@/components/ui";
import { useToast } from "@/components/toast";
import { UserAvatarFace } from "@/components/user-avatar";
import { NotificationDetailModal, type NotificationItem } from "@/components/notification-detail";

type I18n = {
  me: Me | null;
  authReady: boolean;
  locale: Locale;
  tt: (key: string, vars?: Record<string, string | number>) => string;
  theme: "light" | "dark";
  toggleTheme: () => void;
  tickFlashEnabled: boolean;
  tickFlashSeconds: number;
  setTickFlashEnabled: (next: boolean) => Promise<void>;
  refreshMe: () => Promise<void>;
};

const I18nContext = createContext<I18n>({
  me: null,
  authReady: false,
  locale: "zh-Hant",
  tt: (key) => key,
  theme: "light",
  toggleTheme: () => undefined,
  tickFlashEnabled: true,
  tickFlashSeconds: 0.65,
  setTickFlashEnabled: async () => undefined,
  refreshMe: async () => undefined,
});
export function useI18n() {
  return useContext(I18nContext);
}

const SIDEBAR_KEY = "kairos.sidebarCollapsed.v2";
const LOCALE_KEY = "kairos.locale";
const THEME_KEY = "kairos.theme";

type Props = { children: ReactNode };

type NavLeaf = { href: string; label: string; icon: string; exact?: boolean };
type NavItem = NavLeaf & { id?: string; children?: NavItem[] };
type NavNode = NavItem & { id: string };

function isNavGroup(item: NavItem): item is NavNode {
  return Boolean(item.children?.length && item.id);
}

function Icon({ d, className = "" }: { d: string; className?: string }) {
  return (
    <svg className={cn("h-5 w-5 shrink-0", className)} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

const ICONS: Record<string, string> = {
  home: "M3 12l9-9 9 9M5 10v10a1 1 0 001 1h3m10-11v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6",
  portfolio: "M4 7h16M4 12h16M4 17h10",
  bind: "M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1",
  strategies: "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6",
  copy: "M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z",
  referral:
    "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z",
  tasks:
    "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4",
  subscribe:
    "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z",
  settings:
    "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826 3.31-2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z",
  servers:
    "M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01",
  accounts:
    "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z",
  logs: "M9 12h6m-6 4h6M7 4h10a2 2 0 012 2v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6a2 2 0 012-2z",
  terminal: "M8 9l3 3-3 3m5 0h3M5 5h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z",
  menu: "M4 6h16M4 12h16M4 18h16",
  collapse: "M15 19l-7-7 7-7",
  globe: "M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9",
  logout: "M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1",
  clipboard:
    "M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3",
  chevron: "M19 9l-7 7-7-7",
  chevronRight: "M9 5l7 7-7 7",
  sun: "M12 3v2m0 14v2m9-9h-2M5 12H3m15.364 6.364l-1.414-1.414M7.05 7.05L5.636 5.636m12.728 0L16.95 7.05M7.05 16.95l-1.414 1.414M12 8a4 4 0 100 8 4 4 0 000-8z",
  moon: "M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z",
  bell: "M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9",
};

function pathActive(pathname: string, href: string, exact?: boolean) {
  if (exact || href === "/dashboard") return pathname === href;
  return pathname === href || pathname.startsWith(`${href}/`);
}

function descendantActive(item: NavItem, pathname: string): boolean {
  if (item.children?.length) return item.children.some((child) => descendantActive(child, pathname));
  return pathActive(pathname, item.href, item.exact);
}

function flattenLeaves(items: NavItem[]): NavItem[] {
  const out: NavItem[] = [];
  for (const item of items) {
    if (item.children?.length) out.push(...flattenLeaves(item.children));
    else out.push(item);
  }
  return out;
}

export function Shell({ children }: Props) {
  const pathname = usePathname();
  const { toast } = useToast();
  const isApp = pathname.startsWith("/dashboard") || pathname.startsWith("/admin");
  const [me, setMe] = useState<Me | null>(null);
  const [locale, setLocale] = useState<Locale>("zh-Hant");
  const [collapsed, setCollapsed] = useState(false);
  const [theme, setTheme] = useState<"light" | "dark">("light");
  const [allowMotion, setAllowMotion] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [clock, setClock] = useState("");
  const [tickFlashEnabled, setTickFlashEnabledState] = useState(true);
  const [tickFlashSeconds, setTickFlashSeconds] = useState(0.65);
  const [idleTimeoutSeconds, setIdleTimeoutSeconds] = useState(300);
  const [authReady, setAuthReady] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const prevUnreadRef = useRef<number | null>(null);
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  const applyUser = useCallback((user: Me) => {
    setMe(user);
    setLocale(user.locale);
    setTickFlashEnabledState(user.tick_flash_enabled !== false);
    if (typeof user.tick_flash_seconds === "number") setTickFlashSeconds(user.tick_flash_seconds);
    if (typeof user.idle_timeout_seconds === "number") setIdleTimeoutSeconds(user.idle_timeout_seconds);
  }, []);

  useEffect(() => {
    if (!isApp) {
      markAppBootReady();
      return;
    }
    if (authReady) markAppBootReady();
  }, [isApp, authReady]);

  useEffect(() => {
    let cancelled = false;
    if (isApp) setAuthReady(false);
    api<Me>("/v1/auth/me")
      .then((user) => {
        if (!cancelled) applyUser(user);
      })
      .catch(() => {
        if (!cancelled) setMe(null);
      })
      .finally(() => {
        if (!cancelled) setAuthReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isApp, applyUser]);

  // Apply localStorage prefs only after mount so SSR HTML matches the first client render.
  useEffect(() => {
    setCollapsed(window.localStorage.getItem(SIDEBAR_KEY) === "1");
    const stored = window.localStorage.getItem(LOCALE_KEY);
    if (stored === "zh-Hant" || stored === "zh-Hans" || stored === "en") setLocale(stored);
    const savedTheme = window.localStorage.getItem(THEME_KEY) === "dark" ? "dark" : "light";
    setTheme(savedTheme);
    document.documentElement.classList.toggle("dark", savedTheme === "dark");
  }, []);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setAllowMotion(true));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  useEffect(() => {
    document.documentElement.classList.toggle("tick-flash-off", !tickFlashEnabled);
    document.documentElement.style.setProperty("--tick-flash-s", `${tickFlashSeconds}s`);
  }, [tickFlashEnabled, tickFlashSeconds]);

  async function setTickFlashEnabled(next: boolean) {
    setTickFlashEnabledState(next);
    await api("/v1/auth/prefs", { method: "PATCH", body: JSON.stringify({ tick_flash_enabled: next }) });
  }

  const refreshMe = useCallback(async () => {
    applyUser(await api<Me>("/v1/auth/me"));
  }, [applyUser]);

  function toggleCollapsed() {
    setCollapsed((prev) => {
      const next = !prev;
      window.localStorage.setItem(SIDEBAR_KEY, next ? "1" : "0");
      return next;
    });
  }

  function toggleTheme() {
    setTheme((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      window.localStorage.setItem(THEME_KEY, next);
      document.documentElement.classList.toggle("dark", next === "dark");
      return next;
    });
  }

  async function changeLocale(next: Locale) {
    setLocale(next);
    window.localStorage.setItem(LOCALE_KEY, next);
    if (me) await api("/v1/auth/locale", { method: "POST", body: JSON.stringify({ locale: next }) });
  }

  async function logout() {
    await api("/v1/auth/logout", { method: "POST" }).catch(() => undefined);
    window.location.href = "/login";
  }

  useEffect(() => {
    if (!me || idleTimeoutSeconds <= 0) return;
    let last = Date.now();
    const bump = () => {
      last = Date.now();
    };
    const events = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "pointerdown", "click"];
    for (const name of events) window.addEventListener(name, bump, { passive: true });
    const id = window.setInterval(() => {
      if (Date.now() - last < idleTimeoutSeconds * 1000) return;
      window.clearInterval(id);
      void (async () => {
        await api("/v1/auth/logout", { method: "POST" }).catch(() => undefined);
        window.location.href = "/login?idle=1";
      })();
    }, 5000);
    return () => {
      window.clearInterval(id);
      for (const name of events) window.removeEventListener(name, bump);
    };
  }, [me, idleTimeoutSeconds]);

  useEffect(() => {
    if (!me?.id || !isApp) return;
    const id = window.setInterval(() => {
      api<Me>("/v1/auth/me")
        .then((user) => {
          setMe(user);
          setTickFlashEnabledState(user.tick_flash_enabled !== false);
          if (typeof user.tick_flash_seconds === "number") setTickFlashSeconds(user.tick_flash_seconds);
          if (typeof user.idle_timeout_seconds === "number") setIdleTimeoutSeconds(user.idle_timeout_seconds);
        })
        .catch(() => undefined);
    }, 5000);
    return () => window.clearInterval(id);
  }, [me?.id, isApp]);

  const tt = useCallback((key: string, vars?: Record<string, string | number>) => t(locale, key, vars), [locale]);

  useEffect(() => {
    if (!me?.id || !isApp) {
      setUnreadNotifications(0);
      prevUnreadRef.current = null;
      return;
    }
    let cancelled = false;

    const announceNew = (nextCount: number, prevCount: number) => {
      if (nextCount <= prevCount) return;
      // Already in the inbox — badge is enough.
      if (pathnameRef.current.startsWith("/dashboard/messages")) return;
      const jumped = nextCount - prevCount;
      api<{ items: { title?: string }[] }>("/v1/notifications?scope=active&read=unread&page=1&page_size=1")
        .then((res) => {
          if (cancelled) return;
          const title = (res.items?.[0]?.title || "").trim() || tt("messages");
          const message =
            jumped > 1 ? tt("messagesToastMany", { title, n: jumped }) : tt("messagesToastOne", { title });
          toast(message, "info", {
            duration: 10000,
            href: "/dashboard/messages",
          });
        })
        .catch(() => {
          if (cancelled) return;
          toast(tt("messagesToastFallback", { n: jumped }), "info", {
            duration: 10000,
            href: "/dashboard/messages",
          });
        });
    };

    const applyCount = (raw: number) => {
      const next = Number(raw) || 0;
      const prev = prevUnreadRef.current;
      setUnreadNotifications(next);
      if (prev === null) {
        prevUnreadRef.current = next;
        return;
      }
      announceNew(next, prev);
      prevUnreadRef.current = next;
    };

    const refresh = () => {
      api<{ count: number }>("/v1/notifications/unread-count")
        .then((res) => {
          if (!cancelled) applyCount(res.count);
        })
        .catch(() => undefined);
    };
    refresh();
    const id = window.setInterval(refresh, 15000);
    const onChanged = (ev: Event) => {
      const detail = (ev as CustomEvent<{ unread?: number }>).detail;
      if (typeof detail?.unread === "number") applyCount(detail.unread);
      else refresh();
    };
    window.addEventListener("kairos:notifications-changed", onChanged);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.removeEventListener("kairos:notifications-changed", onChanged);
    };
  }, [me?.id, isApp, toast, tt]);

  const clientNav: NavNode[] = useMemo(
    () => [
      { id: "home", href: "/dashboard", label: tt("home"), icon: "home", exact: true },
      {
        id: "accounts",
        href: "/dashboard/portfolio",
        label: tt("accountsMenu"),
        icon: "portfolio",
        children: [
          { href: "/dashboard/portfolio", label: tt("portfolio"), icon: "portfolio" },
          { href: "/dashboard/bind", label: tt("bindNav"), icon: "bind" },
        ],
      },
      {
        id: "execute",
        href: "/dashboard/strategies",
        label: tt("navExecute"),
        icon: "strategies",
        children: [
          { href: "/dashboard/strategies", label: tt("strategies"), icon: "strategies" },
          { href: "/dashboard/copy", label: tt("copy"), icon: "copy" },
        ],
      },
      {
        id: "growth",
        href: "/dashboard/referral",
        label: tt("navGrowth"),
        icon: "referral",
        children: [
          { href: "/dashboard/referral", label: tt("referral"), icon: "referral" },
          { href: "/dashboard/tasks", label: tt("tasks"), icon: "tasks" },
        ],
      },
      { id: "subscribe", href: "/dashboard/subscribe", label: tt("subscribe"), icon: "subscribe" },
      { id: "profile", href: "/dashboard/settings", label: tt("settings"), icon: "settings" },
    ],
    [locale],
  );

  const adminNav: NavNode[] = useMemo(
    () => [
      {
        id: "admin",
        href: "/admin/servers",
        label: tt("admin"),
        icon: "servers",
        children: [
          { href: "/admin/servers", label: tt("servers"), icon: "servers" },
          { href: "/admin/accounts", label: tt("accounts"), icon: "accounts" },
          { href: "/admin/bind-requests", label: tt("bindRequests"), icon: "tasks" },
          { href: "/admin/product-plans", label: tt("productPlans"), icon: "tasks" },
          { href: "/admin/strategy-exclusions", label: tt("strategyExclusions"), icon: "strategies" },
          { href: "/admin/compliance", label: tt("riskTermsAdmin"), icon: "settings" },
          { href: "/admin/strategies", label: tt("adminStrategies"), icon: "strategies" },
          { href: "/admin/invites", label: tt("invites"), icon: "tasks" },
          { href: "/admin/referrals", label: tt("referral"), icon: "referral" },
          {
            id: "brokerLogs",
            href: "/admin/broker-logs",
            label: tt("brokerLogs"),
            icon: "logs",
            children: [
              { href: "/admin/broker-logs", label: tt("brokerLogsJournal"), icon: "logs", exact: true },
              { href: "/admin/broker-logs/console", label: tt("brokerRest"), icon: "terminal" },
            ],
          },
          { href: "/admin/settings", label: tt("adminSettings"), icon: "settings" },
        ],
      },
    ],
    [locale],
  );

  const groups = useMemo(
    () => (me?.role === "admin" ? [...clientNav, ...adminNav] : clientNav),
    [me?.role, clientNav, adminNav],
  );
  const navReady = !isApp || authReady;

  useEffect(() => {
    setOpenGroups((prev) => {
      let changed = false;
      const next = { ...prev };
      const walk = (node: NavNode) => {
        if (!node.children?.length) return false;
        let hit = false;
        for (const child of node.children) {
          if (isNavGroup(child)) {
            if (walk(child)) hit = true;
          } else if (pathActive(pathname, child.href, child.exact)) {
            hit = true;
          }
        }
        if (hit && !next[node.id]) {
          next[node.id] = true;
          changed = true;
        }
        return hit;
      };
      for (const node of groups) walk(node);
      return changed ? next : prev;
    });
  }, [pathname, groups]);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  const pageTitle =
    pathname.startsWith("/dashboard/messages")
      ? tt("messages")
      : flattenLeaves(groups).find((item) => pathActive(pathname, item.href, item.exact))?.label || tt("home");

  const bellMenuProps = { unreadCount: unreadNotifications, tt };
  useEffect(() => {
    const tick = () => setClock(new Date().toISOString().slice(11, 16));
    tick();
    const id = window.setInterval(tick, 30000);
    return () => window.clearInterval(id);
  }, []);

  const localeMenuProps = {
    locale,
    onChange: (next: Locale) => {
      void changeLocale(next);
    },
    label: tt("language"),
  };

  const marketPill = () => (
    <span className="inline-flex max-w-[11rem] items-center gap-1.5 truncate rounded-full border border-[var(--border)] bg-[var(--surface)] px-2.5 py-1 text-[11px] text-[var(--mute)] sm:max-w-none sm:px-3">
      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--positive)]" />
      <span className="truncate">
        {tt("marketsLive")}
        {clock ? ` · ${clock} UTC` : ""}
      </span>
    </span>
  );

  const userMenuProps = {
    me,
    theme,
    onToggleTheme: toggleTheme,
    onLogout: logout,
    tt,
  } as const;

  const marketingHeader = (
    <header className="app-header">
      <Link href={me ? "/dashboard" : "/"} className="flex items-center gap-3">
        <Logo />
        <span className="hidden text-xs text-[var(--mute)] sm:block">Execution OS</span>
      </Link>
      <div className="flex items-center gap-2">
        {me ? (
          <>
            <Link className="rounded-xl px-3 py-2 text-sm text-[var(--text-body)] hover:bg-[var(--nav-hover)]" href="/dashboard">
              {tt("home")}
            </Link>
            {me.role === "admin" && (
              <Link className="rounded-xl px-3 py-2 text-sm text-[var(--text-body)] hover:bg-[var(--nav-hover)]" href="/admin/servers">
                {tt("admin")}
              </Link>
            )}
            <LocaleMenu {...localeMenuProps} />
            <UserMenu {...userMenuProps} />
          </>
        ) : (
          <>
            <Link className="rounded-xl px-3 py-2 text-sm text-[var(--text-body)] hover:bg-[var(--nav-hover)]" href="/login">
              {tt("login")}
            </Link>
            <Link href="/register" className="btn-primary !px-4 !py-2">
              {tt("register")}
            </Link>
            <LocaleMenu {...localeMenuProps} />
            <ThemeButton dark={theme === "dark"} onClick={toggleTheme} label={tt("darkMode")} />
          </>
        )}
      </div>
    </header>
  );

  const rail = (
    <aside
      className={cn(
        "fixed left-0 top-0 z-40 hidden h-svh origin-left flex-col border-r border-[var(--border)] bg-[var(--rail)] backdrop-blur md:flex",
        allowMotion && "transition-[width] duration-200 ease-out",
        collapsed ? "w-[72px] overflow-visible" : "w-64 overflow-hidden",
      )}
    >
      <div className={cn("flex shrink-0 items-center", collapsed ? "flex-col gap-1 p-2 pt-3" : "justify-between gap-1 px-3 pt-3 pb-1")}>
        <Link
          href="/dashboard"
          className={cn("flex items-center rounded-2xl hover:bg-[var(--nav-hover)]", collapsed ? "h-11 w-11 justify-center" : "min-w-0 flex-1 px-1 py-1")}
          title="Kairos"
        >
          <Logo compact markOnly={collapsed} />
        </Link>
        <button
          type="button"
          className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl text-[var(--mute)] hover:bg-[var(--nav-hover)] hover:text-[var(--text)]"
          onClick={toggleCollapsed}
          title={collapsed ? tt("expandSidebar") : tt("collapseSidebar")}
        >
          <Icon d={collapsed ? ICONS.menu : ICONS.collapse} />
        </button>
      </div>
      <nav className={cn("flex-1 space-y-1 pb-3", collapsed ? "overflow-visible px-2" : "overflow-y-auto overflow-x-hidden px-3")}>
        {!navReady ? (
          <div className={cn("px-3 py-2 text-[11px] text-[var(--mute)]", collapsed && "sr-only")}>{tt("loading")}</div>
        ) : (
          groups.map((node) => (
            <NavBlock
              key={node.id}
              node={node}
              pathname={pathname}
              collapsed={collapsed}
              openMap={openGroups}
              onToggle={(id) => setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }))}
            />
          ))
        )}
      </nav>
    </aside>
  );

  const inner = isApp ? (
    <div className="canvas">
      <div className="relative h-screen">
        {rail}
        <div
          className={cn(
            "flex h-full min-w-0 flex-col overflow-hidden",
            collapsed ? "md:pl-[72px]" : "md:pl-64",
            allowMotion && "md:transition-[padding-left] duration-200 ease-out",
          )}
        >
          <header className="dash-topbar !hidden md:!flex">
            <div className="text-lg font-semibold text-[var(--text)]">{pageTitle}</div>
            <div className="ml-auto flex items-center gap-2">
              {marketPill()}
              <BellMenu {...bellMenuProps} />
              <LocaleMenu {...localeMenuProps} />
              <UserMenu {...userMenuProps} />
            </div>
          </header>

          <header className="dash-topbar !flex !px-3 md:!hidden">
            <div className="flex min-w-0 items-center gap-1.5">
              <button
                type="button"
                className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl text-[var(--text)] hover:bg-[var(--nav-hover)]"
                onClick={() => setMobileOpen(true)}
                aria-label={tt("expandSidebar")}
                title={tt("expandSidebar")}
              >
                <Icon d={ICONS.menu} />
              </button>
              <div className="truncate text-base font-semibold text-[var(--text)]">{pageTitle}</div>
            </div>
            <div className="ml-auto flex shrink-0 items-center gap-1.5">
              {marketPill()}
              <BellMenu {...bellMenuProps} />
              <LocaleMenu {...localeMenuProps} />
              <UserMenu {...userMenuProps} />
            </div>
          </header>

          {mobileOpen && (
            <div className="fixed inset-0 z-50 md:hidden">
              <button
                type="button"
                className="absolute inset-0 bg-black/35 backdrop-blur-[1px]"
                aria-label={tt("collapseSidebar")}
                onClick={() => setMobileOpen(false)}
              />
              <aside className="mobile-drawer absolute left-0 top-0 flex h-svh w-[min(18.5rem,86vw)] flex-col border-r border-[var(--border)] bg-[var(--surface-solid)] shadow-[var(--shadow)]">
                <div className="flex shrink-0 items-center justify-between gap-2 px-3 pt-3 pb-1">
                  <Link href="/dashboard" className="flex min-w-0 flex-1 items-center rounded-2xl px-1 py-1 hover:bg-[var(--nav-hover)]" onClick={() => setMobileOpen(false)}>
                    <Logo compact />
                  </Link>
                  <button
                    type="button"
                    className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl text-[var(--mute)] hover:bg-[var(--nav-hover)] hover:text-[var(--text)]"
                    onClick={() => setMobileOpen(false)}
                    aria-label={tt("collapseSidebar")}
                    title={tt("collapseSidebar")}
                  >
                    <Icon d={ICONS.collapse} />
                  </button>
                </div>
                <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-2 pb-4">
                  {!navReady ? (
                    <div className="px-3 py-2 text-[11px] text-[var(--mute)]">{tt("loading")}</div>
                  ) : (
                    groups.map((node) => (
                      <NavBlock
                        key={node.id}
                        node={node}
                        pathname={pathname}
                        collapsed={false}
                        openMap={openGroups}
                        onToggle={(id) => setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }))}
                        onNavigate={() => setMobileOpen(false)}
                      />
                    ))
                  )}
                </nav>
              </aside>
            </div>
          )}

          <main className="flex-1 overflow-auto p-4 md:p-6">
            <div className="mx-auto max-w-7xl animate-fade-in">{children}</div>
          </main>
        </div>
      </div>
    </div>
  ) : (
    <div className="canvas">
      {marketingHeader}
      <main className="mx-auto max-w-6xl px-4 py-12">{children}</main>
    </div>
  );

  return (
    <I18nContext.Provider value={{ me, authReady, locale, tt, theme, toggleTheme, tickFlashEnabled, tickFlashSeconds, setTickFlashEnabled, refreshMe }}>
      {inner}
    </I18nContext.Provider>
  );
}

type BellPreviewItem = {
  id: string;
  title: string;
  body?: string;
  url?: string;
  event?: string;
  created_at?: string | null;
};

function formatBellTime(value?: string | null) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value.replace("T", " ").slice(0, 16);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function BellMenu({
  unreadCount,
  tt,
}: {
  unreadCount: number;
  tt: (key: string, vars?: Record<string, string | number>) => string;
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<BellPreviewItem[]>([]);
  const [detail, setDetail] = useState<NotificationItem | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    api<{ items: BellPreviewItem[] }>("/v1/notifications?scope=active&read=unread&page=1&page_size=3")
      .then((res) => {
        if (!cancelled) setItems(res.items || []);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  async function openPreviewItem(item: BellPreviewItem) {
    setOpen(false);
    setItems((prev) => prev.filter((row) => row.id !== item.id));
    setDetail({
      id: item.id,
      event: item.event || "",
      title: item.title,
      body: item.body || "",
      url: item.url || "",
      created_at: item.created_at,
      is_read: true,
    });
    window.dispatchEvent(
      new CustomEvent("kairos:notifications-changed", {
        detail: { unread: Math.max(0, unreadCount - 1) },
      }),
    );
    try {
      const updated = await api<NotificationItem>(`/v1/notifications/${item.id}/read`, { method: "POST" });
      setDetail(updated);
      const res = await api<{ items: BellPreviewItem[] }>(
        "/v1/notifications?scope=active&read=unread&page=1&page_size=3",
      );
      setItems(res.items || []);
    } catch {
      try {
        const fetched = await api<NotificationItem>(`/v1/notifications/${item.id}`);
        setDetail(fetched);
      } catch {
        /* keep optimistic detail */
      }
    }
  }

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        className="msg-bell"
        onClick={() => setOpen((prev) => !prev)}
        aria-label={tt("messages")}
        aria-expanded={open}
        aria-haspopup="menu"
        title={tt("messages")}
      >
        <Icon d={ICONS.bell} className="h-[18px] w-[18px]" />
        {unreadCount > 0 && (
          <span className="msg-bell__badge">{unreadCount > 99 ? "99+" : unreadCount}</span>
        )}
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-[60] mt-1.5 w-[min(20.5rem,calc(100vw-1.5rem))] overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--surface-solid)] p-1.5 shadow-[var(--shadow)]"
        >
          <div className="px-2.5 py-2 text-xs font-semibold text-[var(--mute)]">{tt("messagesPreviewTitle")}</div>
          <div className="max-h-[18rem] overflow-y-auto">
            {loading ? (
              <div className="px-3 py-6 text-center text-sm text-[var(--mute)]">{tt("loading")}</div>
            ) : items.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-[var(--mute)]">{tt("messagesPreviewEmpty")}</div>
            ) : (
              items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="menuitem"
                  onClick={() => void openPreviewItem(item)}
                  className="block w-full rounded-xl px-3 py-2.5 text-left hover:bg-[var(--nav-hover)]"
                >
                  <div className="flex items-baseline gap-2">
                    <div className="min-w-0 flex-1 truncate text-sm font-medium text-[var(--gold)]">
                      {item.title || tt("messages")}
                    </div>
                    {item.created_at ? (
                      <span className="shrink-0 text-[10px] leading-none text-[var(--mute)]">
                        {formatBellTime(item.created_at)}
                      </span>
                    ) : null}
                  </div>
                  {item.body ? (
                    <div className="mt-0.5 line-clamp-2 text-xs text-[var(--mute)]">{item.body}</div>
                  ) : null}
                </button>
              ))
            )}
          </div>
          <div className="my-1 border-t border-[var(--border)]" />
          <Link
            href="/dashboard/messages"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-[var(--text)] hover:bg-[var(--nav-hover)]"
          >
            <Icon d={ICONS.bell} className="h-[18px] w-[18px] text-[var(--mute)]" />
            <span className="min-w-0 flex-1 truncate text-left">{tt("messagesGoInbox")}</span>
            <Icon d={ICONS.chevronRight} className="h-4 w-4 text-[var(--mute)]" />
          </Link>
        </div>
      )}
      {detail ? <NotificationDetailModal item={detail} onClose={() => setDetail(null)} tt={tt} /> : null}
    </div>
  );
}

function ThemeButton({ dark, onClick, label, compact }: { dark: boolean; onClick: () => void; label: string; compact?: boolean }) {
  const [ready, setReady] = useState(false);
  useEffect(() => setReady(true), []);
  return (
    <button
      type="button"
      className={cn(
        "grid place-items-center rounded-2xl text-[var(--mute)] hover:bg-[var(--nav-hover)] hover:text-[var(--text)]",
        compact ? "h-11 w-11" : "h-9 w-9",
      )}
      onClick={onClick}
      title={label}
    >
      <Icon d={ready && dark ? ICONS.sun : ICONS.moon} />
    </button>
  );
}

function UserMenu({
  me,
  theme,
  onToggleTheme,
  onLogout,
  tt,
}: {
  me: Me | null;
  theme: "light" | "dark";
  onToggleTheme: () => void;
  onLogout: () => void;
  tt: (key: string) => string;
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const email = me?.email || "";
  const displayName = me?.display_name?.trim();
  const roleLabel = me?.role === "admin" ? tt("roleAdmin") : tt("roleClient");
  const referralCode = me?.referral_code?.trim() || "";

  useEffect(() => {
    if (!open) return;
    const onDoc = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 1600);
    return () => window.clearTimeout(id);
  }, [copied]);

  async function copyReferral() {
    if (!referralCode) return;
    try {
      await navigator.clipboard.writeText(referralCode);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        className="rounded-full ring-offset-2 transition hover:ring-2 hover:ring-[var(--gold)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold)]"
        onClick={() => setOpen((prev) => !prev)}
        aria-label={email}
        aria-expanded={open}
        aria-haspopup="menu"
        title={email}
      >
        <UserAvatarFace role={me?.role} avatarId={me?.avatar_id} size="sm" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-[60] mt-1.5 w-[17rem] overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--surface-solid)] p-1.5 shadow-[var(--shadow)]"
        >
          <div className="px-2.5 py-2.5">
            <UserAvatarFace className="mb-2.5" role={me?.role} avatarId={me?.avatar_id} size="md" />
            <div className="truncate text-sm font-semibold text-[var(--text)]">{displayName || email || "—"}</div>
            {displayName && email ? <div className="truncate text-xs text-[var(--mute)]">{email}</div> : null}
            {!displayName && email ? <div className="truncate text-xs text-[var(--mute)]">{roleLabel}</div> : null}
            {displayName ? <div className="mt-0.5 text-[11px] text-[var(--mute)]">{roleLabel}</div> : null}
            {referralCode ? (
              <div className="mt-2 flex items-center gap-1 text-sm text-[var(--mute)]">
                <span className="shrink-0">{tt("referralCode")}</span>
                <button
                  type="button"
                  className="truncate text-left font-mono font-semibold tracking-wide text-[var(--gold)] hover:text-[var(--gold-hover)]"
                  onClick={() => void copyReferral()}
                  title={copied ? tt("copied") : tt("copyReferral")}
                >
                  {referralCode}
                </button>
                <button
                  type="button"
                  className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-[var(--mute)] hover:bg-[var(--nav-hover)] hover:text-[var(--gold)]"
                  onClick={() => void copyReferral()}
                  title={copied ? tt("copied") : tt("copyReferral")}
                  aria-label={copied ? tt("copied") : tt("copyReferral")}
                >
                  <Icon d={ICONS.clipboard} className="h-4 w-4" />
                </button>
              </div>
            ) : null}
          </div>
          <div className="my-1 border-t border-[var(--border)]" />
          <Link
            href="/dashboard/tasks"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-[var(--text)] hover:bg-[var(--nav-hover)]"
          >
            <Icon d={ICONS.tasks} className="h-[18px] w-[18px] text-[var(--mute)]" />
            <span className="min-w-0 flex-1 truncate text-left">{tt("taskCenter")}</span>
            <Icon d={ICONS.chevronRight} className="h-4 w-4 text-[var(--mute)]" />
          </Link>
          <SwitchRow
            className="rounded-xl px-3 py-2.5 hover:bg-[var(--nav-hover)]"
            label={
              <span className="flex items-center gap-3">
                <Icon d={theme === "dark" ? ICONS.sun : ICONS.moon} className="h-[18px] w-[18px] text-[var(--mute)]" />
                {tt("darkMode")}
              </span>
            }
            checked={theme === "dark"}
            onChange={() => onToggleTheme()}
          />
          <Link
            href="/dashboard/settings"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-[var(--text)] hover:bg-[var(--nav-hover)]"
          >
            <Icon d={ICONS.settings} className="h-[18px] w-[18px] text-[var(--mute)]" />
            {tt("settings")}
          </Link>
          <div className="my-1 border-t border-[var(--border)]" />
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-[var(--mute)] hover:bg-[var(--nav-hover)] hover:text-[var(--text)]"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
          >
            <Icon d={ICONS.logout} className="h-[18px] w-[18px]" />
            {tt("logout")}
          </button>
        </div>
      )}
    </div>
  );
}

function LocaleMenu({
  locale,
  onChange,
  label,
}: {
  locale: Locale;
  onChange: (next: Locale) => void;
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        className="msg-bell"
        onClick={() => setOpen((prev) => !prev)}
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="listbox"
        title={label}
      >
        <Icon d={ICONS.globe} className="h-[18px] w-[18px]" />
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute right-0 top-full z-[60] mt-1.5 min-w-[7.5rem] overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--surface-solid)] p-1 shadow-[var(--shadow)]"
        >
          {LOCALES.map((item) => (
            <button
              key={item}
              type="button"
              role="option"
              aria-selected={item === locale}
              className={cn(
                "flex w-full items-center rounded-xl px-3 py-2 text-left text-sm transition",
                item === locale
                  ? "bg-[var(--gold-soft)] font-semibold text-[var(--gold)]"
                  : "text-[var(--text)] hover:bg-[var(--nav-hover)]",
              )}
              onClick={() => {
                onChange(item);
                setOpen(false);
              }}
            >
              {LOCALE_LABEL[item]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function NavBlock({
  node,
  pathname,
  collapsed,
  openMap,
  onToggle,
  onNavigate,
}: {
  node: NavNode;
  pathname: string;
  collapsed: boolean;
  openMap: Record<string, boolean>;
  onToggle: (id: string) => void;
  onNavigate?: () => void;
}) {
  const [fly, setFly] = useState(false);
  const open = !!openMap[node.id];
  const childActive = descendantActive(node, pathname);
  const selfActive = pathActive(pathname, node.href, node.exact) && !node.children;
  const showKids = !!node.children && (open || childActive);

  function renderLeaf(child: NavItem, nested = true) {
    return (
      <NavLink
        key={`${child.href}-${child.exact ? "exact" : "pre"}`}
        href={child.href}
        active={pathActive(pathname, child.href, child.exact)}
        icon={ICONS[child.icon]}
        collapsed={false}
        label={child.label}
        nested={nested}
        onClick={() => {
          setFly(false);
          onNavigate?.();
        }}
      />
    );
  }

  if (collapsed && node.children) {
    const leaves = flattenLeaves(node.children);
    return (
      <div className="relative" onMouseEnter={() => setFly(true)} onMouseLeave={() => setFly(false)}>
        <button
          type="button"
          title={node.label}
          aria-expanded={fly}
          onClick={() => setFly((prev) => !prev)}
          className={cn(
            "mx-auto grid h-11 w-11 place-items-center rounded-2xl text-sm transition duration-200",
            childActive ? "bg-[var(--gold-soft)] font-semibold text-[var(--gold)]" : "text-[var(--nav)] hover:bg-[var(--nav-hover)] hover:text-[var(--text)]",
          )}
        >
          <Icon d={ICONS[node.icon]} />
        </button>
        {fly && (
          <div className="absolute left-full top-0 z-50 pl-2">
            <div className="min-w-[12rem] rounded-2xl border border-[var(--border)] bg-[var(--surface-solid)] p-1.5 shadow-[var(--shadow)]">
              <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--mute)]">{node.label}</div>
              {leaves.map((child) => renderLeaf(child, true))}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (!node.children) {
    return (
      <NavLink
        href={node.href}
        active={selfActive}
        icon={ICONS[node.icon]}
        collapsed={collapsed}
        label={node.label}
        onClick={onNavigate}
      />
    );
  }

  return (
    <div>
      <button
        type="button"
        title={collapsed ? node.label : undefined}
        onClick={() => onToggle(node.id)}
        className={cn(
          "flex w-full items-center rounded-2xl text-sm transition duration-200",
          collapsed ? "mx-auto h-11 w-11 justify-center" : "gap-3 px-3 py-2.5",
          childActive ? "bg-[var(--gold-soft)] font-semibold text-[var(--gold)]" : "text-[var(--nav)] hover:bg-[var(--nav-hover)] hover:text-[var(--text)]",
        )}
      >
        <Icon d={ICONS[node.icon]} />
        {!collapsed && <span className="min-w-0 flex-1 truncate text-left">{node.label}</span>}
        {!collapsed && <Icon d={ICONS.chevron} className={cn("h-4 w-4 transition", showKids && "rotate-180")} />}
      </button>
      {showKids && (
        <div className="ml-4 mt-1 space-y-0.5 border-l border-[var(--border)] pl-2">
          {node.children.map((child) =>
            isNavGroup(child) ? (
              <NavBlock
                key={child.id}
                node={child}
                pathname={pathname}
                collapsed={false}
                openMap={openMap}
                onToggle={onToggle}
                onNavigate={onNavigate}
              />
            ) : (
              renderLeaf(child)
            ),
          )}
        </div>
      )}
    </div>
  );
}

function NavLink({
  href,
  active,
  icon,
  collapsed,
  label,
  nested,
  onClick,
}: {
  href: string;
  active: boolean;
  icon: string;
  collapsed: boolean;
  label: string;
  nested?: boolean;
  onClick?: () => void;
}) {
  return (
    <Link
      href={href}
      title={collapsed ? label : undefined}
      onClick={onClick}
      className={cn(
        "flex items-center rounded-2xl text-sm transition duration-200",
        collapsed ? "mx-auto h-11 w-11 justify-center" : nested ? "gap-2 px-3 py-2" : "gap-3 px-3 py-2.5",
        active ? "bg-[var(--gold-soft)] font-semibold text-[var(--gold)]" : "text-[var(--nav)] hover:bg-[var(--nav-hover)] hover:text-[var(--text)]",
      )}
    >
      <Icon d={icon} />
      {!collapsed && <span className="whitespace-nowrap">{label}</span>}
    </Link>
  );
}

export function useMe() {
  const { me, authReady } = useI18n();
  return { me, loading: !authReady };
}
